import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { KitDeletionDialog } from "@/components/dependency-deletion/KitDeletionDialog";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import {
  GroupedTable,
  GroupedTableColumn,
} from "@/components/GroupedTable/GroupedTable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Column, Row } from "@/components/ui/flex";
import { Input } from "@/components/ui/input";
import { useKitsData } from "@/data/use-kits-data";
import { useUsersData } from "@/data/use-users-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { groupByDate, smartDate } from "@/lib/date";
import { GetKitDto } from "@/lib/gen-api";
import { getInitials } from "@/lib/string";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClone,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useState } from "react";
import { toast } from "sonner";

export default function PresetsPage() {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState("");

  const { kits, createKit } = useKitsData(groupUuid, {
    type: "project_preset",
    owned: true,
  });

  const groupedPresets = groupByDate(kits, "date_updated");

  const columns: GroupedTableColumn<GetKitDto>[] = [
    { key: "name", label: "Name" },
    { key: "date_updated", label: "Last Modified" },
    { key: "user_uuid", label: "Created By" },
  ];

  function handleAddPreset() {
    setIsDialogOpen(true);
  }

  function handleCreatePreset() {
    if (presetName.trim()) {
      const name = presetName.trim();

      createKit({
        name,
      }).then(async (kit) => {
        if (!kit) {
          toast.error("Failed to create kit. Please try again.");
          return;
        }

        toast.success("Preset created successfully!");
      });

      setPresetName("");
      setIsDialogOpen(false);
    }
  }

  async function handleClonePreset(kit: GetKitDto) {
    await createKit({
      name: `${kit.name} (cloned)`,
      type: "project_preset",
      presetKitUuid: kit.uuid,
    });
    toast.success("Preset cloned successfully!");
  }

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Group Workbook Templates</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <Column>
          <p>
            Workbook Templates let you build blueprints for entire marketing
            workbooks. Define all the documents, goals, Expert AIs, and workflow
            steps. Once set, these templates make it easy for your AIs to run
            complex workbooks with minimal input, so you can launch campaigns
            faster and more consistently.
          </p>
        </Column>
        <Row>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleAddPreset}>
                Create New Workbook Template
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create New Workbook Template</DialogTitle>
                <DialogDescription>
                  Enter a name for your new Workbook Template.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Input
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="Name"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCreatePreset();
                    }
                  }}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button type="submit" onClick={handleCreatePreset} size="sm">
                  Create Preset
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Row>
        <GroupedTable
          data={groupedPresets}
          columns={columns}
          itemIdKey="uuid"
          renderCell={{
            name: (_, row) => <KitDisplayName kit={row} />,
            user_uuid: (value) => <UserCell userUuid={value} />,
            date_updated: (value) => smartDate(value),
          }}
          renderActions={({ row }) => (
            <PresetRowActions row={row} onClone={handleClonePreset} />
          )}
          rowLink={(row) => ({
            to: "/w/$groupSlug/k/$kitUuid/preset-settings",
            params: { groupSlug, kitUuid: row.uuid },
          })}
          emptyMessage="No workbook templates here yet. Create your first one to set up repeatable marketing workbooks that your AIs can run with minimal input"
        />
      </PageBody>
    </Page>
  );
}

function UserCell({ userUuid }: { userUuid: string }) {
  const groupUuid = useGroupUuid();
  const { getUserForUserUuid } = useUsersData(groupUuid);
  return (
    <Row className="items-center gap-2">
      <Avatar className="h-5 w-5">
        <AvatarFallback>
          {getInitials(getUserForUserUuid(userUuid)?.name || "", 2)}
        </AvatarFallback>
        <AvatarImage
          src={getUserForUserUuid(userUuid)?.photo_url ?? undefined}
        />
      </Avatar>
      <span>{getUserForUserUuid(userUuid)?.name}</span>
    </Row>
  );
}

function PresetRowActions({
  row,
  onClone,
}: {
  row: GetKitDto;
  onClone: (kit: GetKitDto) => void;
}) {
  return (
    <Row className="items-center gap-2">
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-destructive hover:text-destructive-foreground"
            aria-label="Clone"
            title="Clone"
          >
            <FontAwesomeIcon icon={faClone} />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clone {row.name}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onClone(row);
              }}
              asChild
            >
              <Button size="sm">Clone</Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <KitDeletionDialog kitUuid={row.uuid} groupUuid={row.group_uuid}>
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-destructive hover:text-destructive-foreground"
          aria-label="Delete"
          title="Delete"
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </KitDeletionDialog>
    </Row>
  );
}
